import { useId, type InputHTMLAttributes, type ReactNode } from "react";

type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "id"> & {
  label: string;
  /** Server or client validation message. Turns the field red and is announced to screen readers. */
  error?: string | undefined;
  hint?: ReactNode;
  leadingIcon?: ReactNode;
  /** Element inside the field on the right, e.g. a show-password toggle. */
  trailing?: ReactNode;
  /** Small text on the right of the label row. */
  labelAside?: ReactNode;
};

export function TextField({
  label,
  error,
  hint,
  leadingIcon,
  trailing,
  labelAside,
  className,
  ...inputProps
}: TextFieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = error ? errorId : hint ? hintId : undefined;

  return (
    <div className={className}>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-label-md uppercase text-on-surface-variant">
          {label}
        </label>
        {labelAside}
      </div>

      <div
        className={`flex h-13 items-center gap-3 rounded-xl border bg-surface-container-low px-4 transition-colors focus-within:bg-surface-container ${
          error
            ? "border-error/70"
            : "border-outline-variant/60 focus-within:border-primary-container/70"
        }`}
      >
        {leadingIcon ? (
          <span className="shrink-0 text-on-surface-variant" aria-hidden>
            {leadingIcon}
          </span>
        ) : null}
        <input
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className="h-full min-w-0 flex-1 bg-transparent text-body-lg text-on-surface placeholder:text-outline focus:outline-none"
          {...inputProps}
        />
        {trailing}
      </div>

      {error ? (
        <p id={errorId} role="alert" className="mt-1.5 text-body-sm text-error">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="mt-1.5 text-body-sm text-on-surface-variant">
          {hint}
        </p>
      ) : null}
    </div>
  );
}